import { ComuniTopic } from '../constants';
import { withTopicPrefix } from './topicPrefix';

export type ComuniChannel = 'whatsapp' | 'sms' | 'email';

export type WhatsappModelFormData = {
  template_name: string;
  template_language: string;
  template_category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  parameter_format: 'POSITIONAL' | 'NAMED';
  template_header_text?: string;
  template_body_text: string;
  template_footer_text?: string;
  header_examples?: string[];
  body_examples?: string[];
};

export type SmsModelFormData = {
  template_name: string;
  template_text: string;
};

export type EmailModelFormData = {
  template_name: string;
  template_category: string;
  template_subject: string;
  template_body_html: string;
  template_body_text?: string;
};

export type SystemModel =
  | { id: string; topic: ComuniTopic; channel: 'whatsapp'; name: string; formData: WhatsappModelFormData }
  | { id: string; topic: ComuniTopic; channel: 'sms'; name: string; formData: SmsModelFormData }
  | { id: string; topic: ComuniTopic; channel: 'email'; name: string; formData: EmailModelFormData };

/** Testo mostrato nelle card e come corpo dell'anteprima. */
export const modelPreviewText = (model: SystemModel): string => {
  switch (model.channel) {
    case 'whatsapp':
      return model.formData.template_body_text;
    case 'sms':
      return model.formData.template_text;
    case 'email':
      return model.formData.template_subject;
  }
};

const wa = (
  id: string,
  topic: ComuniTopic,
  name: string,
  body: string,
  bodyExamples: string[],
  category: WhatsappModelFormData['template_category'] = 'UTILITY'
): SystemModel => ({
  id,
  topic,
  channel: 'whatsapp',
  name,
  formData: {
    template_name: withTopicPrefix(topic, id.replace(/-/g, '_')),
    template_language: 'it',
    template_category: category,
    parameter_format: 'POSITIONAL',
    template_body_text: body,
    body_examples: bodyExamples,
  },
});

const sms = (id: string, topic: ComuniTopic, name: string, text: string): SystemModel => ({
  id,
  topic,
  channel: 'sms',
  name,
  formData: { template_name: withTopicPrefix(topic, name), template_text: text },
});

const email = (id: string, topic: ComuniTopic, name: string, subject: string, bodyHtml: string, bodyText: string): SystemModel => ({
  id,
  topic,
  channel: 'email',
  name,
  formData: {
    template_name: withTopicPrefix(topic, name),
    template_category: 'Comunicazioni Comune',
    template_subject: subject,
    template_body_html: bodyHtml,
    template_body_text: bodyText,
  },
});

/**
 * Modelli di sistema shipped con la UI (contenuti dal design V0 Comuni). Ogni `template_name` porta il
 * prefisso del topic (`<topic>__…`, vedi topicPrefix.ts): Personalizza lo precompila e il template creato
 * resta nel gruppo del modello.
 */
export const SYSTEM_MODELS: SystemModel[] = [
  // ── Chiusura strade ──────────────────────────────────────────────
  wa(
    'lavori-rete-idrica',
    'strade',
    'Lavori rete idrica',
    'Gentile {{1}}, il Comune informa che {{2}} resterà chiusa al traffico dal {{3}} ore {{4}} al {{5}} ore {{6}} per lavori urgenti alla rete idrica. Percorsi alternativi segnalati in loco.',
    ['Maria', 'Via Roma', '09/07/2026', '08:00', '11/07/2026', '18:00']
  ),
  sms(
    'lavori-rete-idrica-sms',
    'strade',
    'Lavori rete idrica (SMS)',
    'Comune: {{via}} chiusa al traffico dal {{data_inizio}} h {{ora_inizio}} al {{data_fine}} h {{ora_fine}} per lavori alla rete idrica.'
  ),
  email(
    'lavori-rete-idrica-email',
    'strade',
    'Lavori rete idrica (Email)',
    'Chiusura {{via}} — avviso del Comune',
    '<p>Gentile {{nome}},</p><p>il Comune informa che {{via}} resterà chiusa al traffico dal {{data_inizio}} ore {{ora_inizio}} al {{data_fine}} ore {{ora_fine}} per lavori urgenti alla rete idrica.</p><p>Percorsi alternativi segnalati in loco.</p>',
    'Gentile {{nome}}, il Comune informa che {{via}} resterà chiusa al traffico dal {{data_inizio}} ore {{ora_inizio}} al {{data_fine}} ore {{ora_fine}} per lavori urgenti alla rete idrica.'
  ),
  wa(
    'mercato-settimanale',
    'strade',
    'Mercato settimanale',
    'Gentile {{1}}, si informa che {{2}} sarà chiusa al traffico il {{3}} dalle ore {{4}} alle ore {{5}} per il mercato settimanale. Percorsi alternativi segnalati in loco.',
    ['Maria', 'Via Roma', '10/07/2026', '06:00', '15:00']
  ),
  wa(
    'manifestazione-corteo',
    'strade',
    'Manifestazione / corteo',
    'Gentile {{1}}, in occasione di {{2}}, {{3}} sarà chiusa al transito il {{4}} dalle ore {{5}} alle ore {{6}}. Seguire la segnaletica temporanea.',
    ['Maria', 'Festa dello sport', 'Viale delle Fosse', '12/07/2026', '09:00', '20:00']
  ),
  wa(
    'lavori-stradali-programmati',
    'strade',
    'Lavori stradali programmati',
    'Gentile {{1}}, il Comune informa che {{2}} sarà interessata da lavori di manutenzione del manto stradale dal {{3}} al {{4}}. Possibili rallentamenti e chiusure temporanee.',
    ['Maria', 'Via Piave', '14/07/2026', '18/07/2026']
  ),
  wa(
    'emergenza-incidente',
    'strade',
    'Emergenza / incidente',
    'Gentile {{1}}, {{2}} è temporaneamente chiusa al traffico a seguito di un intervento di emergenza. Vi aggiorneremo alla riapertura.',
    ['Maria', 'Via Brenta']
  ),
  // ── Allerta meteo ────────────────────────────────────────────────
  wa(
    'allerta-gialla',
    'meteo',
    'Allerta gialla',
    "Gentile {{1}}, la Protezione Civile ha emesso un'allerta gialla per {{2}} dalle ore {{3}} di {{4}}. Si raccomanda prudenza negli spostamenti.",
    ['Maria', 'temporali', '14:00', '09/07/2026']
  ),
  wa(
    'allerta-arancione-rossa',
    'meteo',
    'Allerta arancione/rossa',
    'ATTENZIONE {{1}}: allerta {{2}} per {{3}} dalle ore {{4}} di {{5}}. Evitare gli spostamenti non necessari e seguire le indicazioni della Protezione Civile.',
    ['Maria', 'arancione', 'piena del fiume', '16:00', '09/07/2026']
  ),
  sms(
    'allerta-meteo-sms',
    'meteo',
    'Allerta meteo (SMS)',
    'Comune: allerta {{livello}} {{fenomeno}} dal {{data}}. Evitare spostamenti non necessari. Info sul sito del Comune.'
  ),
  // ── Protezione civile / sisma ────────────────────────────────────
  wa(
    'evento-sismico',
    'sisma',
    'Evento sismico',
    'Messaggio della Protezione Civile: si è verificato un evento sismico in {{1}} alle ore {{2}}. In caso di necessità seguire le vie di fuga e raggiungere le aree di attesa. Aggiornamenti a seguire.',
    ['zona Valrovina', '11:32']
  ),
  wa(
    'punti-di-raccolta',
    'sisma',
    'Punti di raccolta',
    'Si invita la popolazione di {{1}} a recarsi presso i punti di raccolta: {{2}}. Seguire le indicazioni dei volontari della Protezione Civile.',
    ['Campese', 'piazzale della scuola primaria']
  ),
  sms(
    'emergenza-sms',
    'sisma',
    'Emergenza (SMS)',
    'PROTEZIONE CIVILE: {{istruzioni}}. Seguire le indicazioni delle autorità.'
  ),
  // ── Interruzione idrica ──────────────────────────────────────────
  wa(
    'sospensione-idrica-programmata',
    'idrico',
    'Sospensione programmata',
    'Gentile {{1}}, il servizio idrico sarà sospeso in {{2}} dalle ore {{3}} alle ore {{4}} del {{5}} per lavori di manutenzione.',
    ['Maria', 'zona Centro storico', '08:00', '17:00', '15/07/2026']
  ),
  wa(
    'ripristino-servizio-idrico',
    'idrico',
    'Ripristino servizio',
    'Gentile {{1}}, il servizio idrico è stato ripristinato in {{2}}. Ci scusiamo per il disagio.',
    ['Maria', 'zona Centro storico']
  ),
  email(
    'sospensione-idrica-email',
    'idrico',
    'Sospensione idrica (Email)',
    'Sospensione servizio idrico {{zona}} — avviso del Comune',
    '<p>Gentile {{nome}},</p><p>il servizio idrico sarà sospeso in {{zona}} dalle ore {{ora_inizio}} alle ore {{ora_fine}} del {{data}} per lavori di manutenzione.</p><p>Ci scusiamo per il disagio.</p>',
    'Gentile {{nome}}, il servizio idrico sarà sospeso in {{zona}} dalle ore {{ora_inizio}} alle ore {{ora_fine}} del {{data}} per lavori di manutenzione.'
  ),
  // ── Interruzione energia elettrica ───────────────────────────────
  wa(
    'interruzione-elettrica-programmata',
    'elettrico',
    'Interruzione programmata',
    "Gentile {{1}}, è programmata un'interruzione dell'energia elettrica in {{2}} il {{3}} dalle ore {{4}} alle ore {{5}} per lavori sulla rete.",
    ['Maria', 'via Pigafetta e limitrofe', '16/07/2026', '08:30', '12:30']
  ),
  sms(
    'interruzione-elettrica-sms',
    'elettrico',
    'Interruzione programmata (SMS)',
    'Comune: interruzione elettrica {{zona}} il {{data}} ore {{ora_inizio}}-{{ora_fine}} per lavori sulla rete.'
  ),
  // ── Eventi e manifestazioni ──────────────────────────────────────
  wa(
    'invito-evento',
    'eventi',
    'Invito evento',
    'Gentile {{1}}, il Comune la invita a {{2}} il {{3}} alle ore {{4}} presso {{5}}. La cittadinanza è invitata a partecipare.',
    ['Maria', 'Notte bianca', '19/07/2026', '18:00', 'centro storico'],
    'MARKETING'
  ),
  wa(
    'variazioni-viabilita-evento',
    'eventi',
    'Variazioni viabilità evento',
    'Gentile {{1}}, in occasione di {{2}} sono previste variazioni alla viabilità in {{3}} il {{4}}. Consultare le ordinanze sul sito del Comune.',
    ['Maria', 'Notte bianca', 'centro storico', '19/07/2026']
  ),
  // ── Scadenze tributi ─────────────────────────────────────────────
  wa(
    'promemoria-scadenza-tributo',
    'tributi',
    'Promemoria scadenza',
    'Gentile {{1}}, ricordiamo la scadenza {{2}} del {{3}}. È possibile pagare tramite PagoPA o presso gli sportelli abilitati.',
    ['Maria', 'TARI — seconda rata', '31/07/2026']
  ),
  email(
    'promemoria-scadenza-email',
    'tributi',
    'Promemoria scadenza (Email)',
    'Scadenza {{tributo}} il {{data}} — avviso del Comune',
    '<p>Gentile {{nome}},</p><p>ricordiamo la scadenza {{tributo}} del {{data}}.</p><p>È possibile pagare tramite PagoPA o presso gli sportelli abilitati.</p>',
    'Gentile {{nome}}, ricordiamo la scadenza {{tributo}} del {{data}}. È possibile pagare tramite PagoPA o presso gli sportelli abilitati.'
  ),
];
